import React, { useEffect, useState, useCallback } from 'react';
import config from '../../../config/config';
import { useNavigate } from 'react-router-dom';
import './dashboard.css';

function Dashboard() {
  const navigate = useNavigate();
  const userType = localStorage.getItem('userType');

  const [stats, setStats] = useState({
    totalProperties: 0,
    totalUsers: 0,
    totalBanners: 0,
    totalContacts: 0,
  });
  const [recentData, setRecentData] = useState({
    properties: [],
    users: [],
    contacts: [],
  });
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel
      const [propertiesRes, usersRes, bannersRes, contactsRes] = await Promise.all([
        config.getAllProperties().catch(() => ({ data: [] })),
        config.getAllUsers().catch(() => ({ data: [] })),
        config.getAllBanners().catch(() => ({ data: [] })),
        config.contactusFormData().catch(() => ({ data: [] })),
      ]);

      const properties = propertiesRes.data || [];
      const users = usersRes.data || [];
      const banners = bannersRes.data || [];
      const contacts = contactsRes.data || [];

      // Get user type from localStorage
      const userType = localStorage.getItem('userType');
      const userId = localStorage.getItem('userId');

      // Filter properties by user if not admin (userType !== 1)
      let filteredProperties = properties;
      if (userType !== '1' && userId) {
        filteredProperties = properties.filter(p => p.userId === userId);
      }

      setStats({
        totalProperties: filteredProperties.length,
        totalUsers: users.length,
        totalBanners: banners.length,
        totalContacts: contacts.length,
      });

      setRecentData({
        properties: filteredProperties.slice(0, 5),
        users: users.slice(0, 5),
        contacts: contacts.slice(0, 5),
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const StatCard = ({ title, value, icon, color, link }) => (
    <div
      className={`stat-card stat-card-${color}`}
      onClick={() => link && navigate(link)}
      style={{ cursor: link ? 'pointer' : 'default' }}
    >
      <div className="stat-card-content">
        <div className="stat-icon">
          <i className={`fas ${icon}`}></i>
        </div>
        <div className="stat-info">
          <h3 className="stat-title">{title}</h3>
          <p className="stat-value">{value}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="page-inner">
      <div className="page-header">
        <h3 className="fw-bold mb-3">Dashboard</h3>
        <ul className="breadcrumbs mb-3">
          <li className="nav-home">
            <a href="#">
              <i className="fas fa-home"></i>
            </a>
          </li>
          <li className="separator">
            <i className="fa-solid fa-greater-than"></i>
          </li>
          <li className="nav-item">
            <a href="#">Dashboard</a>
          </li>
        </ul>
      </div>

      {loading ? (
        <div className="text-center" style={{ padding: '40px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading dashboard data...</p>
        </div>
      ) : (
        <>
          {userType === "1" && (
            <>
              {/* Statistics Cards */}
              <div className="row mb-4">
            <div className="col-md-3 col-sm-6 mb-3">
              <StatCard
                title="Total Properties"
                value={stats.totalProperties}
                icon="fa-home"
                color="primary"
                link="/admin/property"
              />
            </div>
            <div className="col-md-3 col-sm-6 mb-3">
              <StatCard
                title="Total Users"
                value={stats.totalUsers}
                icon="fa-users"
                color="success"
                link="/admin/user"
              />
            </div>
            <div className="col-md-3 col-sm-6 mb-3">
              <StatCard
                title="Total Banners"
                value={stats.totalBanners}
                icon="fa-image"
                color="info"
                link="/admin/banner"
              />
            </div>
            <div className="col-md-3 col-sm-6 mb-3">
              <StatCard
                title="Contact Forms"
                value={stats.totalContacts}
                icon="fa-envelope"
                color="warning"
                link="/admin/contact-us"
              />
            </div>
          </div>

          {/* Recent Properties Section */}
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <div className="card-title">
                    <i className="fas fa-home"></i> Recent Properties
                  </div>
                </div>
                <div className="card-body">
                  {recentData.properties.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover table-sm">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Phone</th>
                            <th>Country</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentData.properties.map((prop) => (
                            <tr key={prop._id}>
                              <td className="text-truncate" style={{ maxWidth: '100px' }}>
                                {prop.name}
                              </td>
                              <td>{prop.phone}</td>
                              <td>{prop.country}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-info"
                                  onClick={() => navigate(`/admin/property/editProperty/${prop._id}`)}
                                  style={{ padding: '0.25rem 0.5rem', fontSize: '11px' }}
                                >
                                  View
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted">No properties yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* Recent Users Section */}
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <div className="card-title">
                    <i className="fas fa-users"></i> Recent Users
                  </div>
                </div>
                <div className="card-body">
                  {recentData.users.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover table-sm">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentData.users.map((user) => (
                            <tr key={user._id}>
                              <td className="text-truncate" style={{ maxWidth: '100px' }}>
                                {user.name}
                              </td>
                              <td className="text-truncate" style={{ maxWidth: '120px', fontSize: '12px' }}>
                                {user.email}
                              </td>
                              <td>{user.phone}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-info"
                                  onClick={() => navigate(`/admin/user/editUser/${user._id}`)}
                                  style={{ padding: '0.25rem 0.5rem', fontSize: '11px' }}
                                >
                                  View
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted">No users yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Contact Forms Section */}
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-header">
                  <div className="card-title">
                    <i className="fas fa-envelope"></i> Recent Contact Forms
                  </div>
                </div>
                <div className="card-body">
                  {recentData.contacts.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover table-sm">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Subject</th>
                            <th>Message</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentData.contacts.map((contact, index) => (
                            <tr key={contact._id}>
                              <td>{index + 1}</td>
                              <td className="text-truncate" style={{ maxWidth: '100px' }}>
                                {contact.name}
                              </td>
                              <td className="text-truncate" style={{ maxWidth: '120px', fontSize: '12px' }}>
                                {contact.email}
                              </td>
                              <td>{contact.phone}</td>
                              <td className="text-truncate" style={{ maxWidth: '120px' }}>
                                {contact.subject}
                              </td>
                              <td className="text-truncate" style={{ maxWidth: '200px' }}>
                                {contact.message}
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-info"
                                  onClick={() => navigate(`/admin/contact-us/editContactUsForm/${contact._id}`)}
                                  style={{ padding: '0.25rem 0.5rem', fontSize: '11px' }}
                                >
                                  View
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted">No contact forms yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default Dashboard;
